<template>
    <li v-show="isVisible(node)" class="py-sm fade-item h-fit" :class="{ 'border-l border-(--border-subtle)': depth > 0 }">
        <!-- Parent node -->
        <div class="gap-xs flex">
            <button
                class="nodes-center visible-on-hover ml-sm flex h-8 w-full p-0"
                :class="{
                    'has-children': node.children.length > 0,
                    expanded: isActive(node) && !multiple,
                }"
                tabindex="0"
                @click.prevent="(e) => (multiple && toggleSelection ? toggleSelection(e, node) : toggle(e, node))"
                @keydown.enter.prevent="(e) => (multiple && toggleSelection ? toggleSelection(e, node) : toggle(e, node))"
                @keydown.space.prevent="(e) => (multiple && toggleSelection ? toggleSelection(e, node) : toggle(e, node))"
            >
                <div
                    class="gap-sm rounded-xsm pl-s nodes-center px-xs flex h-full w-full items-center transition-colors duration-300 ease-linear hover:bg-(--surface-highlight)"
                    :class="{
                        'border-l-2 border-(--border-focus) bg-(--surface-highlight)': isActive(node) && !multiple,
                        'bg-(--surface-highlight)': isSelected && isSelected(node),
                    }"
                >
                    <i
                        v-if="node.children.length > 0"
                        class="i-dropdown-arrow block transition-transform duration-300"
                        :class="{ rotated: isExpanded(node) }"
                        @click.stop="toggle($event, node)"
                    ></i>
                     <!-- Spacer for alignment if no children -->
                    <div v-else class="w-5 h-5"></div>
                    
                    <div v-if="multiple" class="flex items-center">
                        <i :class="isSelected && isSelected(node) ? 'i-checkbox-checked' : 'i-checkbox-unchecked'" class="text-(--icon-primary)"></i>
                    </div>

                    <i
                        :class="{
                            // note: it is not safe to use string manipulation like `i-${node.locationType}`, the whole string 'i-region' needs to be here for Tailwind processor to pick it up,
                            'i-region': node.locationType === 'region',
                            'i-site': node.locationType === 'site',
                            'i-building': node.locationType === 'building',
                            'i-floor': node.locationType === 'floor',
                            'i-room': node.locationType === 'room',
                            'i-pod': node.locationType === 'pod',
                            'i-rack': node.locationType === 'rack',
                            'i-rack_row': node.locationType === 'rack_row',
                            'i-request_type': node.locationType === 'request_type',
                            'i-aisle': node.locationType === 'aisle',
                        }"
                        class="h-5 w-5"
                    ></i>
                    <p class="custom-link" :class="{ 'text-(--link-default)': (isActive(node) && !multiple) || (isSelected && isSelected(node)) }">
                        {{ node.name }}
                    </p>
                </div>
            </button>
        </div>

        <!-- Recursive Children -->

        <ul v-if="isExpanded(node)" class="ml-[42px]">
            <LocationNode
                v-for="child in [...node.children].sort((a, b) => a.name.localeCompare(b.name))"
                :key="child.id"
                :node="child"
                :search="search"
                :is-expanded="isExpanded"
                :is-active="isActive"
                :toggle="toggle"
                :is-visible="isVisible"
                :multiple="multiple"
                :is-selected="isSelected"
                :toggle-selection="toggleSelection"
                :depth="depth + 1"
                class="border-left border-(--border-strong)"
            />
        </ul>
    </li>
</template>

<script setup lang="ts">
import type { UUIDLocation } from '@/stores/store-interfaces';

defineProps({
    node: {
        type: Object as () => UUIDLocation,
        required: true,
    },
    search: {
        type: String,
        required: true,
    },
    isExpanded: {
        type: Function,
        required: true,
    },
    isActive: {
        type: Function,
        required: true,
    },
    toggle: {
        type: Function, // Toggles expansion
        required: true,
    },
    isVisible: {
        type: Function,
        required: true,
    },
    multiple: {
        type: Boolean,
        default: false,
    },
    isSelected: {
        type: Function,
        default: undefined,
    },
    toggleSelection: {
        type: Function,
        default: undefined,
    },
    depth: {
        type: Number,
        default: 0,
    },
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';
.i-dropdown-arrow {
    transition: transform 0.3s ease;
}

.i-dropdown-arrow.rotated {
    transform: rotate(180deg);
}

.visible-on-hover {
    .visible-toggle-item {
        display: none;
    }

    &:hover,
    &:focus {
        .visible-toggle-item {
            display: block;
        }
    }
}

.custom-link {
    font-weight: 400;
    font-family: 'Matter-Regular';
    @apply text-md hover:underline;
}
</style>
